/* First-party, cookieless visitor telemetry client.
   - Pageview on every route change (wired by `useVisitTracking` in App.js)
   - Named events (`sim_open`, `pdf_download`, etc.) via `track(event, meta)`
   - Uses `fetch` with `keepalive:true` so an in-flight beacon survives the
     page unload (no need for `navigator.sendBeacon`, which can't send custom
     headers and would trip the CSRF middleware for authed users)
   - `session_id` lives in `sessionStorage` — NOT a cookie — so it dies with
     the tab and never carries across sessions (privacy-friendly by design)
   - Silent failures — telemetry MUST NOT ever break a page. */

import { resolveBackendUrl } from "./backendUrl";

const SESSION_KEY = "vetsim_session";

function ensureSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      // Prefer crypto.randomUUID when available (all modern browsers) — fall
      // back to timestamp+random so we still produce something in ancient WKWebView.
      sid = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    // Private-browsing mode can throw on sessionStorage — degrade to per-call random.
    return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function readCsrfCookie() {
  try {
    const parts = (document.cookie || "").split(";");
    for (const raw of parts) {
      const [k, ...rest] = raw.trim().split("=");
      if (k === "csrf_token") return decodeURIComponent(rest.join("="));
    }
  } catch { /* ignore */ }
  return null;
}

async function send(pathname, body) {
  try {
    const base = resolveBackendUrl();
    if (!base) return;
    const csrf = readCsrfCookie();
    await fetch(`${base}${pathname}`, {
      method: "POST",
      keepalive: true, // survives page-unload navigations
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    /* telemetry is best-effort — never surface an error */
  }
}

// Full-referrer is helpful; anything longer than 400 chars is truncated to keep
// the payload tight (server enforces the same cap defensively).
function safeReferrer() {
  try {
    const r = document.referrer || "";
    // Skip self-referrers so client-side route changes don't clutter the top-referrer list.
    if (r && new URL(r).host === window.location.host) return null;
    return r || null;
  } catch { return null; }
}

/* Public API */

export function trackVisit(pathname) {
  const path = pathname || (window.location.pathname + window.location.search);
  return send("/api/track/visit", {
    path,
    referrer: safeReferrer(),
    session_id: ensureSessionId(),
    screen_w: window.screen?.width || null,
    screen_h: window.screen?.height || null,
  });
}

export function track(event, meta) {
  return send("/api/track/event", {
    event,
    session_id: ensureSessionId(),
    path: window.location.pathname,
    meta: meta || {},
  });
}

/* Small convenience helpers so call sites are self-documenting. */
export const trackPricingView   = ()   => track("pricing_view");
export const trackLandingView   = ()   => track("landing_view");
export const trackPdfDownload   = (id) => track("pdf_download", { sheet_id: id });
export const trackSignupSuccess = ()   => track("signup_success");
export const trackLoginSuccess  = ()   => track("login_success");
export const trackSimOpen       = (id) => track("sim_open", { sim_id: id });
export const trackSimComplete   = (id, score, grade) => track("sim_complete", { sim_id: id, score, grade });
export const trackUpgradeClick  = (src) => track("upgrade_click", { source: src });
export const trackCheckoutStart = (tier) => track("checkout_start", { tier });
